import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShowcase } from "./AppShowcase";
import { useT, useLp, useLang } from "../lib/i18n";
import { currentSourcePage, trackDownloadClick } from "../lib/plausible";
import { getFounding100Remaining } from "../lib/api";
import { CURRENCIES, type Currency, loadCurrency } from "../lib/currency";

const ptFeatureHighlights = [
  "Sem bot na call",
  "Responde pergunta difícil em inglês",
  "Funciona em Zoom/Meet/Teams",
];

export function Hero() {
  const t = useT();
  const copy = t.hero;
  const lp = useLp();
  const lang = useLang();
  const [foundingRemaining, setFoundingRemaining] = useState<number | null>(null);
  const [currency, setCurrency] = useState<Currency>(() => loadCurrency());

  useEffect(() => {
    if (lang !== "pt") return;

    let cancelled = false;
    getFounding100Remaining()
      .then(({ remaining }) => {
        if (cancelled) return;
        setFoundingRemaining(Math.max(0, Math.min(100, remaining)));
      })
      .catch(() => {
        if (!cancelled) setFoundingRemaining(null);
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => setCurrency(loadCurrency()), []);

  const foundingCounter =
    lang === "pt"
      ? foundingRemaining !== null
        ? `${foundingRemaining} vagas Founding 100 restantes`
        : "Founding 100: primeiras 100 vagas"
      : null;
  const currencyInfo = CURRENCIES[currency];
  const heroFootnote =
    lang === "pt"
      ? `macOS · Windows · grátis por 7 dias · ${currencyInfo.foundingPriceFormatted}/mês nas primeiras 100 vagas (12 meses travado)`
      : copy.footnote;

  return (
    <section className="relative bg-paper-ruled">
      <div className="mx-auto max-w-6xl px-6 pt-24 sm:pt-32 pb-20 text-center">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-ink/10 bg-paper/50 px-3 py-1 text-xs font-medium text-ink/70 backdrop-blur dark:border-paper/10 dark:bg-ink/50 dark:text-paper/70">
            <span className="h-1.5 w-1.5 rounded-full bg-rec" />
            {copy.badge}
          </div>
          {foundingCounter ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-rec/25 bg-rec/10 px-3 py-1 text-xs font-semibold text-ink/70 backdrop-blur dark:text-paper/75">
              <span className="h-1.5 w-1.5 rounded-full bg-rec" />
              {foundingCounter}
            </div>
          ) : null}
        </div>

        <h1 className="mx-auto mt-6 max-w-4xl font-display text-5xl sm:text-7xl font-semibold tracking-tightest leading-[1.02] text-ink dark:text-paper">
          {copy.h1Line1}
          <br />
          {copy.h1Line2}
        </h1>

        <p className="mt-6 mx-auto max-w-xl text-lg sm:text-xl text-ink/70 dark:text-paper/70 leading-relaxed">
          {copy.description}
        </p>

        {lang === "pt" ? (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {ptFeatureHighlights.map((feature) => (
              <span
                key={feature}
                className="rounded-full border border-ink/10 bg-paper/60 px-3 py-1.5 text-xs font-medium text-ink/70 dark:border-paper/10 dark:bg-ink/40 dark:text-paper/70"
              >
                {feature}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to={lp + "/downloads"}
            onClick={() => trackDownloadClick({ source_page: currentSourcePage(), lang, os: "unknown" })}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
          >
            {copy.ctaPrimary}
            <span className="text-xs opacity-70">{copy.ctaPrimarySub}</span>
          </Link>
          <a
            href="#copilot"
            className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-sm font-medium text-ink hover:bg-ink/5 transition-colors dark:border-paper/20 dark:text-paper dark:hover:bg-paper/5"
          >
            {copy.ctaSecondary}
            <span className="text-xs opacity-70">{copy.ctaSecondarySub}</span>
          </a>
        </div>

        <p className="mt-6 text-xs text-ink/50 dark:text-paper/50">
          {heroFootnote}
        </p>

        <AppShowcase />
      </div>
    </section>
  );
}
